import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import {
    Plus,
    Loader2,
    FolderPlus,
    Download,
    Upload,
    ChevronsDown,
    ChevronsUp,
    LayoutList,
    Folder,
    Search,
    CheckSquare,
    Square,
    Percent,
    MoveRight,
    Trash2,
} from "lucide-react";
import {
    getTemplateTree,
    updateTemplate,
    createTemplateCategory,
    downloadTemplateCsv,
    importTemplateCsv,
    updateTemplateItem,
    deleteTemplateItem,
} from "../lib/api";
import CategoryBlock from "./CategoryBlock";
import TemplateItemRow from "./TemplateItemRow";
import InlineEditText from "./InlineEditText";

export default function TemplateEditor({ template, onTemplateUpdated }) {
    const [tree, setTree] = useState(null);
    const [loading, setLoading] = useState(true);
    const [addingCat, setAddingCat] = useState(false);
    const [importing, setImporting] = useState(false);
    const fileInputRef = useRef(null);
    // Two monotonic counters that are passed down to every category/item row.
    // Bumping collapseKey snaps every row closed; bumping expandKey opens
    // them all. Starts undefined so freshly-loaded tabs don't trigger either
    // and simply honour each row's own default (collapsed).
    const [collapseKey, setCollapseKey] = useState(undefined);
    const [expandKey, setExpandKey] = useState(undefined);
    // iter-80: "Items only" flat view + search. The flat list is built
    // from the same `tree.categories[*].items` source — TemplateItemRow
    // is rendered directly, with the category name shown as a small
    // pill so users still know where each item lives.
    const [viewMode, setViewMode] = useState("categories");
    const [searchQuery, setSearchQuery] = useState("");

    // iter-81 — bulk-action state for the Items-only view.
    // `selected` holds top-level item IDs only (sub-items aren't
    // rendered in the flat list, so they can't be selected here).
    const [selected, setSelected] = useState(() => new Set());
    const [bulkBusy, setBulkBusy] = useState(false);
    const [bumpPct, setBumpPct] = useState("");
    const [moveTargetCat, setMoveTargetCat] = useState("");
    // Reset selection whenever the view-mode changes — selections in
    // categories mode wouldn't be visible to act on.
    useEffect(() => {
        setSelected(new Set());
    }, [viewMode]);
    const toggleSelected = useCallback((id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    // Top-level item update helper used by the flat view (CategoryBlock
    // owns this logic in the grouped view).
    const handleFlatItemUpdated = useCallback((catId, updated) => {
        setTree((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                categories: (prev.categories || []).map((c) => {
                    if (c.id !== catId) return c;
                    return {
                        ...c,
                        items: (c.items || []).map((it) =>
                            it.id === updated.id ? { ...it, ...updated } : it,
                        ),
                    };
                }),
            };
        });
    }, []);
    const handleFlatItemDeleted = useCallback((catId, itemId) => {
        setTree((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                categories: (prev.categories || []).map((c) => {
                    if (c.id !== catId) return c;
                    return {
                        ...c,
                        items: (c.items || []).filter((it) => it.id !== itemId),
                    };
                }),
            };
        });
    }, []);

    // Bulk action runners — keep selection visible until the run
    // finishes so the user sees what they touched, then clear.
    const runBulk = async (label, fn) => {
        if (selected.size === 0) return;
        setBulkBusy(true);
        let ok = 0;
        let fail = 0;
        try {
            // Process sequentially — keeps server load gentle and lets
            // a partial failure leave a coherent partial state.
            for (const id of Array.from(selected)) {
                try {
                    await fn(id);
                    ok++;
                } catch {
                    fail++;
                }
            }
            if (fail === 0) toast.success(`${label} applied to ${ok} item${ok === 1 ? "" : "s"}`);
            else toast.error(`${label}: ${ok} succeeded, ${fail} failed`);
            // Always reload so prices/categories reflect server state.
            const fresh = await getTemplateTree(template.id);
            setTree(fresh);
            setSelected(new Set());
        } finally {
            setBulkBusy(false);
        }
    };

    const handleBulkBump = () => {
        const pct = Number(bumpPct);
        if (!Number.isFinite(pct) || pct === 0) {
            toast.error("Enter a non-zero percentage (e.g. 5 or -10).");
            return;
        }
        if (
            !window.confirm(
                `Apply ${pct > 0 ? "+" : ""}${pct}% to ${selected.size} item${selected.size === 1 ? "" : "s"}?`,
            )
        ) {
            return;
        }
        const factor = 1 + pct / 100;
        // Build a quick lookup of current prices so we don't depend on
        // the row-level state during the run.
        const priceById = {};
        for (const c of tree.categories || []) {
            for (const it of c.items || []) {
                priceById[it.id] = Number(it.price) || 0;
            }
        }
        runBulk(`${pct > 0 ? "+" : ""}${pct}%`, async (id) => {
            const newPrice = Math.round(priceById[id] * factor * 100) / 100;
            await updateTemplateItem(id, { price: newPrice });
        });
        setBumpPct("");
    };

    const handleBulkMove = () => {
        if (!moveTargetCat) {
            toast.error("Pick a destination category.");
            return;
        }
        const targetCat = (tree.categories || []).find((c) => c.id === moveTargetCat);
        const tName = targetCat?.name || "category";
        if (
            !window.confirm(
                `Move ${selected.size} item${selected.size === 1 ? "" : "s"} to "${tName}"?`,
            )
        ) {
            return;
        }
        runBulk(`Moved to "${tName}"`, async (id) => {
            await updateTemplateItem(id, { category_id: moveTargetCat });
        });
        setMoveTargetCat("");
    };

    const handleBulkDelete = () => {
        if (
            !window.confirm(
                `Delete ${selected.size} item${selected.size === 1 ? "" : "s"}? This cannot be undone.`,
            )
        ) {
            return;
        }
        runBulk("Deleted", async (id) => {
            await deleteTemplateItem(id);
        });
    };

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await getTemplateTree(template.id);
            setTree(data);
        } catch (e) {
            toast.error("Couldn't load template");
        } finally {
            setLoading(false);
        }
    }, [template.id]);

    useEffect(() => {
        load();
    }, [load]);

    const saveTemplate = async (patch) => {
        try {
            const updated = await updateTemplate(template.id, patch);
            onTemplateUpdated(updated);
            setTree((prev) => (prev ? { ...prev, ...updated } : prev));
        } catch (e) {
            toast.error("Couldn't update template");
        }
    };

    const handleAddCategory = async () => {
        setAddingCat(true);
        try {
            const created = await createTemplateCategory(template.id, {
                name: "New category",
            });
            setTree((prev) =>
                prev
                    ? {
                          ...prev,
                          categories: [...(prev.categories || []), { ...created, items: [] }],
                      }
                    : prev,
            );
        } catch (e) {
            toast.error("Couldn't add category");
        } finally {
            setAddingCat(false);
        }
    };

    const handleCategoryUpdated = (catId, patch) => {
        setTree((prev) =>
            prev
                ? {
                      ...prev,
                      categories: (prev.categories || []).map((c) =>
                          c.id === catId ? { ...c, ...patch } : c,
                      ),
                  }
                : prev,
        );
    };

    const handleCategoryDeleted = (catId) => {
        setTree((prev) =>
            prev
                ? { ...prev, categories: (prev.categories || []).filter((c) => c.id !== catId) }
                : prev,
        );
    };

    const handleExportCsv = async () => {
        try {
            await downloadTemplateCsv(template.id, tree?.name || template.name);
            toast.success("CSV downloaded");
        } catch (e) {
            toast.error("Couldn't export CSV");
        }
    };

    const handleImportFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";  // reset so same file can be picked again later
        if (!file) return;
        const currentItemCount = (tree?.categories || []).reduce(
            (acc, c) => acc + (c.items?.length || 0),
            0,
        );
        let mode = "append";
        if (currentItemCount > 0) {
            const replace = window.confirm(
                `Import "${file.name}"\n\n` +
                `This template already has ${currentItemCount} item(s). Click:\n` +
                `  • OK to REPLACE everything with the CSV contents, or\n` +
                `  • Cancel to APPEND the CSV on top of existing items.`,
            );
            mode = replace ? "replace" : "append";
        }
        setImporting(true);
        try {
            const res = await importTemplateCsv(template.id, file, mode);
            toast.success(
                `Imported ${res.imported_items} item(s) into ${res.created_categories} new categor${res.created_categories === 1 ? "y" : "ies"}`,
            );
            await load();
        } catch (err) {
            const detail = err?.response?.data?.detail || "Couldn't import CSV";
            toast.error(detail);
        } finally {
            setImporting(false);
        }
    };

    if (loading || !tree) {
        return (
            <div className="flex items-center justify-center py-16 text-slate-400 text-sm">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading template…
            </div>
        );
    }

    const totalItems = (tree.categories || []).reduce(
        (acc, c) => acc + (c.items?.length || 0),
        0,
    );

    // Flatten top-level items for the "Items only" view, annotated
    // with their owning category for breadcrumbs and search.
    const flatRows = (tree.categories || []).flatMap((c) =>
        (c.items || [])
            .filter((it) => !it.parent_item_id) // sub-items live under their parent
            .map((it) => ({ item: it, category: c })),
    );
    const filteredFlat = (() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return flatRows;
        return flatRows.filter(({ item, category }) =>
            (item.name || "").toLowerCase().includes(q)
            || (item.description || "").toLowerCase().includes(q)
            || (category.name || "").toLowerCase().includes(q),
        );
    })();

    return (
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 pb-24" data-testid="template-editor">
            <div className="mb-8 space-y-4">
                <div className="min-w-0">
                    <InlineEditText
                        value={tree.name}
                        onSave={(v) => saveTemplate({ name: v })}
                        placeholder="Template name"
                        className="text-2xl md:text-3xl font-bold text-white"
                        dataTestId="template-name-input"
                    />
                    <InlineEditText
                        value={tree.description || ""}
                        onSave={(v) => saveTemplate({ description: v })}
                        placeholder="Add a description (e.g. 2026 pricing for residential projects)"
                        className="text-sm text-slate-400 mt-1"
                        dataTestId="template-desc-input"
                        multiline
                    />
                    <div className="mt-3 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">
                        {(tree.categories || []).length} categories · {totalItems} items
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <button
                        onClick={() => setCollapseKey((k) => (k || 0) + 1)}
                        data-testid="btn-collapse-all"
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-sm bg-[#1C253C] hover:bg-[#223055] border border-[#2A3655] hover:border-[#00A3FF] text-xs font-bold uppercase tracking-wider text-slate-200 hover:text-white transition-colors"
                        title="Collapse every category and item in this template"
                    >
                        <ChevronsUp className="w-3.5 h-3.5 text-[#00A3FF]" />
                        Collapse all
                    </button>
                    <button
                        onClick={() => setExpandKey((k) => (k || 0) + 1)}
                        data-testid="btn-expand-all"
                        disabled={viewMode === "flat"}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-sm bg-[#1C253C] hover:bg-[#223055] border border-[#2A3655] hover:border-[#00A3FF] text-xs font-bold uppercase tracking-wider text-slate-200 hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        title="Expand every category in this template (line items stay collapsed)"
                    >
                        <ChevronsDown className="w-3.5 h-3.5 text-[#00A3FF]" />
                        Expand all
                    </button>
                    <button
                        onClick={() =>
                            setViewMode((m) => (m === "flat" ? "categories" : "flat"))
                        }
                        data-testid="btn-view-mode"
                        className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-sm border text-xs font-bold uppercase tracking-wider transition-colors ${
                            viewMode === "flat"
                                ? "bg-[#00A3FF] hover:bg-[#3395FF] border-[#00A3FF] text-white"
                                : "bg-[#1C253C] hover:bg-[#223055] border-[#2A3655] hover:border-[#00A3FF] text-slate-200 hover:text-white"
                        }`}
                        title={
                            viewMode === "flat"
                                ? "Switch back to category view"
                                : "Show every item in one searchable list"
                        }
                    >
                        {viewMode === "flat" ? (
                            <>
                                <Folder className="w-3.5 h-3.5" />
                                Categories
                            </>
                        ) : (
                            <>
                                <LayoutList className="w-3.5 h-3.5 text-[#00A3FF]" />
                                Items only
                            </>
                        )}
                    </button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,text/csv"
                        className="hidden"
                        onChange={handleImportFile}
                        data-testid="template-csv-input"
                    />
                    <button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={importing}
                        data-testid="btn-import-csv"
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-sm bg-[#1C253C] hover:bg-[#223055] border border-[#2A3655] hover:border-[#00A3FF] text-xs font-bold uppercase tracking-wider text-slate-200 hover:text-white transition-colors disabled:opacity-60"
                        title="Import items from a CSV (append or replace)"
                    >
                        {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 text-[#00A3FF]" />}
                        Import CSV
                    </button>
                    <button
                        onClick={handleExportCsv}
                        data-testid="btn-export-csv"
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-sm bg-[#1C253C] hover:bg-[#223055] border border-[#2A3655] hover:border-[#00A3FF] text-xs font-bold uppercase tracking-wider text-slate-200 hover:text-white transition-colors"
                        title="Download all items as a CSV"
                    >
                        <Download className="w-3.5 h-3.5 text-[#00A3FF]" />
                        Export CSV
                    </button>
                </div>
            </div>

            {(tree.categories || []).length === 0 ? (
                <div className="text-center py-12 border border-dashed border-[#2A3655] rounded-md bg-[#0F1B3A]/30">
                    <FolderPlus className="w-10 h-10 text-[#00A3FF] mx-auto mb-3 opacity-70" />
                    <p className="text-slate-300 text-sm mb-4">
                        No categories yet. Start by grouping your services.
                    </p>
                    <button
                        onClick={handleAddCategory}
                        disabled={addingCat}
                        data-testid="btn-add-first-category"
                        className="inline-flex items-center gap-2 bg-[#007AFF] hover:bg-[#3395FF] disabled:opacity-60 text-white font-bold uppercase tracking-wider px-5 py-2.5 rounded-sm text-xs"
                    >
                        {addingCat ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                        Add first category
                    </button>
                </div>
            ) : viewMode === "flat" ? (
                <div className="space-y-3" data-testid="flat-items-view">
                    <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                        <input
                            type="search"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder={`Search ${flatRows.length} item${flatRows.length === 1 ? "" : "s"}…`}
                            data-testid="flat-items-search"
                            className="w-full pl-9 pr-3 py-2.5 text-sm bg-[#0A1128] border border-[#2A3655] rounded-sm text-white placeholder:text-slate-500 focus:border-[#00A3FF] outline-none"
                            autoFocus
                        />
                        {searchQuery && (
                            <button
                                type="button"
                                onClick={() => setSearchQuery("")}
                                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-wider font-bold text-slate-400 hover:text-white"
                            >
                                Clear
                            </button>
                        )}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-bold flex items-center gap-3 flex-wrap">
                        <span>
                            Showing {filteredFlat.length} of {flatRows.length} items
                        </span>
                        {filteredFlat.length > 0 && (
                            <button
                                type="button"
                                onClick={() => {
                                    const allSelected = filteredFlat.every(({ item }) =>
                                        selected.has(item.id),
                                    );
                                    setSelected((prev) => {
                                        const next = new Set(prev);
                                        if (allSelected) {
                                            for (const { item } of filteredFlat) next.delete(item.id);
                                        } else {
                                            for (const { item } of filteredFlat) next.add(item.id);
                                        }
                                        return next;
                                    });
                                }}
                                data-testid="flat-select-all"
                                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-[#00A3FF] hover:text-white"
                            >
                                {filteredFlat.every(({ item }) => selected.has(item.id))
                                    ? "Deselect all"
                                    : "Select all visible"}
                            </button>
                        )}
                    </div>
                    {selected.size > 0 && (
                        <BulkActionBar
                            count={selected.size}
                            categories={tree.categories || []}
                            bumpPct={bumpPct}
                            setBumpPct={setBumpPct}
                            moveTargetCat={moveTargetCat}
                            setMoveTargetCat={setMoveTargetCat}
                            onBump={handleBulkBump}
                            onMove={handleBulkMove}
                            onDelete={handleBulkDelete}
                            onClear={() => setSelected(new Set())}
                            busy={bulkBusy}
                        />
                    )}
                    {filteredFlat.length === 0 ? (
                        <div className="text-sm text-slate-500 italic py-6 text-center">
                            {searchQuery
                                ? "No items match that search."
                                : "No items yet — add a category and start adding items."}
                        </div>
                    ) : (
                        <div className="space-y-1.5">
                            {filteredFlat.map(({ item, category }) => {
                                const subItems = (category.items || []).filter(
                                    (it) => it.parent_item_id === item.id,
                                );
                                return (
                                    <div
                                        key={item.id}
                                        className={`space-y-1 ${selected.has(item.id) ? "ring-1 ring-[#00A3FF] rounded-sm" : ""}`}
                                        data-testid={`flat-row-${item.id}`}
                                    >
                                        <div className="flex items-center gap-2 pl-1">
                                            <button
                                                type="button"
                                                onClick={() => toggleSelected(item.id)}
                                                aria-pressed={selected.has(item.id)}
                                                data-testid={`flat-select-${item.id}`}
                                                className="inline-flex items-center justify-center p-0.5 rounded-sm shrink-0"
                                            >
                                                {selected.has(item.id) ? (
                                                    <CheckSquare className="w-4 h-4 text-[#00A3FF]" />
                                                ) : (
                                                    <Square className="w-4 h-4 text-slate-500 hover:text-[#00A3FF]" />
                                                )}
                                            </button>
                                            <span className="text-[9px] uppercase tracking-[0.16em] text-[#00A3FF] font-bold">
                                                {category.name}
                                            </span>
                                            {subItems.length > 0 && (
                                                <span className="text-[9px] uppercase tracking-[0.16em] text-slate-500 font-normal">
                                                    · {subItems.length} sub-item{subItems.length === 1 ? "" : "s"}
                                                </span>
                                            )}
                                        </div>
                                        <TemplateItemRow
                                            item={item}
                                            subItems={subItems}
                                            onUpdated={(updated) =>
                                                handleFlatItemUpdated(category.id, updated)
                                            }
                                            onDeleted={() =>
                                                handleFlatItemDeleted(category.id, item.id)
                                            }
                                            onSubItemCreated={(created) =>
                                                handleFlatItemUpdated(category.id, {
                                                    ...item,
                                                    items: [...subItems, created],
                                                })
                                            }
                                            onSubItemUpdated={(subId, updated) =>
                                                handleFlatItemUpdated(category.id, updated)
                                            }
                                            onSubItemDeleted={(subId) =>
                                                handleFlatItemDeleted(category.id, subId)
                                            }
                                            collapseKey={collapseKey}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            ) : (
                <div className="space-y-4">
                    {(tree.categories || []).map((cat) => (
                        <CategoryBlock
                            key={cat.id}
                            category={cat}
                            onUpdated={(patch) => handleCategoryUpdated(cat.id, patch)}
                            onDeleted={() => handleCategoryDeleted(cat.id)}
                            collapseKey={collapseKey}
                            expandKey={expandKey}
                        />
                    ))}
                </div>
            )}

            {(tree.categories || []).length > 0 && (
                <div className="mt-6">
                    <button
                        onClick={handleAddCategory}
                        disabled={addingCat}
                        data-testid="btn-add-category"
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-sm bg-[#1C253C] hover:bg-[#223055] border border-dashed border-[#2A3655] hover:border-[#00A3FF] text-sm font-semibold text-slate-200 hover:text-white transition-colors"
                    >
                        {addingCat ? <Loader2 className="w-4 h-4 animate-spin" /> : <FolderPlus className="w-4 h-4 text-[#00A3FF]" />}
                        Add category
                    </button>
                </div>
            )}
        </div>
    );
}

/**
 * iter-81 — sticky bulk-action bar that appears when one or more items
 * are selected in the flat (Items only) view. Stays out of the way at
 * other times and never tries to show in the categories view (selection
 * is reset when the user toggles modes).
 */
function BulkActionBar({
    count,
    categories,
    bumpPct,
    setBumpPct,
    moveTargetCat,
    setMoveTargetCat,
    onBump,
    onMove,
    onDelete,
    onClear,
    busy,
}) {
    return (
        <div
            className="sticky top-2 z-10 p-3 rounded-md bg-[#0F1B3A] border border-[#00A3FF] shadow-lg shadow-[#00A3FF]/20 flex flex-wrap items-center gap-3"
            data-testid="bulk-action-bar"
        >
            <span className="text-xs font-bold text-white">
                <span
                    className="inline-flex items-center justify-center min-w-[24px] h-6 px-2 rounded-full bg-[#00A3FF] text-[11px] tabular-nums mr-2"
                    data-testid="bulk-action-count"
                >
                    {count}
                </span>
                selected
            </span>
            {/* Apply % bump */}
            <div
                className="flex items-center gap-1 bg-[#0A1128] border border-[#2A3655] rounded-sm overflow-hidden"
                data-testid="bulk-bump-group"
            >
                <Percent className="w-3 h-3 text-slate-500 ml-2" />
                <input
                    type="number"
                    step="0.5"
                    value={bumpPct}
                    onChange={(e) => setBumpPct(e.target.value)}
                    placeholder="5 or -10"
                    inputMode="decimal"
                    data-testid="bulk-bump-pct"
                    className="w-16 bg-transparent border-0 outline-none px-1 py-1 text-xs tabular-nums text-white placeholder:text-slate-500"
                />
                <button
                    type="button"
                    onClick={onBump}
                    disabled={busy || !bumpPct}
                    data-testid="bulk-bump-apply"
                    className="px-2 py-1 text-[11px] font-bold uppercase tracking-wider bg-[#1C253C] hover:bg-[#2A3655] text-white border-l border-[#2A3655] disabled:opacity-40"
                >
                    Apply %
                </button>
            </div>
            {/* Move to category */}
            <div
                className="flex items-center gap-1 bg-[#0A1128] border border-[#2A3655] rounded-sm overflow-hidden"
                data-testid="bulk-move-group"
            >
                <MoveRight className="w-3 h-3 text-slate-500 ml-2" />
                <select
                    value={moveTargetCat}
                    onChange={(e) => setMoveTargetCat(e.target.value)}
                    data-testid="bulk-move-cat"
                    className="bg-[#0A1128] border-0 outline-none px-1 py-1 text-xs text-white max-w-[160px]"
                >
                    <option value="" className="bg-[#0A1128] text-white">
                        Move to…
                    </option>
                    {categories.map((c) => (
                        <option
                            key={c.id}
                            value={c.id}
                            className="bg-[#0A1128] text-white"
                        >
                            {c.name}
                        </option>
                    ))}
                </select>
                <button
                    type="button"
                    onClick={onMove}
                    disabled={busy || !moveTargetCat}
                    data-testid="bulk-move-apply"
                    className="px-2 py-1 text-[11px] font-bold uppercase tracking-wider bg-[#1C253C] hover:bg-[#2A3655] text-white border-l border-[#2A3655] disabled:opacity-40"
                >
                    Move
                </button>
            </div>
            {/* Delete */}
            <button
                type="button"
                onClick={onDelete}
                disabled={busy}
                data-testid="bulk-delete"
                className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-bold uppercase tracking-wider rounded-sm bg-red-500/15 hover:bg-red-500/25 text-red-300 hover:text-red-200 border border-red-500/40 disabled:opacity-40"
            >
                <Trash2 className="w-3 h-3" />
                Delete
            </button>
            <div className="flex-1" />
            {busy ? (
                <Loader2 className="w-4 h-4 animate-spin text-[#00A3FF]" />
            ) : (
                <button
                    type="button"
                    onClick={onClear}
                    data-testid="bulk-clear"
                    className="text-[11px] uppercase tracking-wider font-bold text-slate-400 hover:text-white"
                >
                    Clear
                </button>
            )}
        </div>
    );
}
